import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  CreditCard,
  Lock,
  AlertTriangle,
  CheckCircle2,
  Building2,
  Info,
  Landmark,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import { useFormatting } from '@/hooks/useFormatting';
import { useApp } from '@/contexts/AppContext';
import { useToast } from '@/hooks/use-toast';
import { BANK_ACCOUNTS, MANUAL_FINANCING_FACILITIES } from '@/data/financial-systems';
import { getAllBankAccounts, updateBankAccount } from '@/api/services/bankAccountService';
import { mapBankAccountFromApi, mapBankAccountToUpdateRequest } from '@/api/mappers/bankAccountMapper';
import { ApiError } from '@/api/client';
import type {
  BankAccount,
  CreditFacility,
  CreditFacilityType,
  InvoiceFinanceType,
  AssetFinanceType,
  InterestRateEntry,
  DrawdownEntry,
  ManualFinancingFacility,
  ModeType,
  RestrictedFund,
  RestrictionType,
} from '@/types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function facilityTypeLabel(t: CreditFacilityType | undefined): string {
  if (!t) return 'facility';
  const labels: Record<CreditFacilityType, string> = {
    overdraft: 'Overdraft',
    revolving_credit: 'Revolving Credit Facility',
    invoice_finance: 'Invoice Finance',
    asset_finance: 'Asset Finance',
  };
  return labels[t];
}

function restrictionTypeLabel(t: RestrictionType): string {
  const labels: Record<RestrictionType, string> = {
    client_money: 'Client Money / Trust Fund',
    fixed_term: 'Fixed Term Deposit',
    notice_account: 'Notice Account',
    escrow: 'Escrow / Retention',
    minimum_balance: 'Minimum Balance Requirement',
    internal_reserve: 'Internal Reserve',
  };
  return labels[t];
}

function restrictionTypeBadgeColour(t: RestrictionType): string {
  const colours: Record<RestrictionType, string> = {
    client_money: 'bg-red-100 text-red-800',
    fixed_term: 'bg-blue-100 text-blue-800',
    notice_account: 'bg-amber-100 text-amber-800',
    escrow: 'bg-purple-100 text-purple-800',
    minimum_balance: 'bg-slate-100 text-slate-700',
    internal_reserve: 'bg-green-100 text-green-700',
  };
  return colours[t];
}

function availableCash(account: BankAccount): number {
  const restricted = (account.restrictedFunds ?? []).reduce((s, r) => s + r.restrictedAmount, 0);
  const facilityHeadroom =
    account.creditFacility && account.creditFacility.includeInAvailableCash !== false
      ? account.creditFacility.facilityLimit - account.creditFacility.facilityDrawn
      : 0;
  return account.balance - restricted + facilityHeadroom;
}

function totalRestricted(account: BankAccount): number {
  return (account.restrictedFunds ?? []).reduce((s, r) => s + r.restrictedAmount, 0);
}

function headroom(account: BankAccount): number {
  if (!account.creditFacility) return 0;
  if (account.creditFacility.includeInAvailableCash === false) return 0;
  return account.creditFacility.facilityLimit - account.creditFacility.facilityDrawn;
}

function formatDateShort(d: Date | undefined): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Blank templates ──────────────────────────────────────────────────────────

function blankFacility(): CreditFacility {
  return {
    type: 'overdraft',
    facilityLimit: 0,
    facilityDrawn: 0,
    includeInAvailableCash: true,
  };
}

function blankRestriction(): Omit<RestrictedFund, 'id'> {
  return {
    restrictionType: 'internal_reserve',
    label: '',
    restrictedAmount: 0,
    isLegal: false,
  };
}

function blankRateEntry(): InterestRateEntry {
  return {
    id: `rate-${Date.now()}`,
    effectiveFrom: new Date(),
    rate: 0,
    note: '',
  };
}

function blankDrawdown(): DrawdownEntry {
  return {
    id: `dd-${Date.now()}`,
    date: new Date(),
    amount: 0,
    description: '',
  };
}

// ─── Interest Rate Schedule sub-component ────────────────────────────────────

interface InterestRateScheduleProps {
  schedule: InterestRateEntry[];
  onChange: (updated: InterestRateEntry[]) => void;
}

function InterestRateSchedule({ schedule, onChange }: InterestRateScheduleProps) {
  function addEntry() {
    onChange([...schedule, blankRateEntry()]);
  }

  function updateEntry(id: string, partial: Partial<InterestRateEntry>) {
    onChange(schedule.map(e => e.id === id ? { ...e, ...partial } : e));
  }

  function removeEntry(id: string) {
    onChange(schedule.filter(e => e.id !== id));
  }

  const sorted = [...schedule].sort((a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime());

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3" />
          Interest rate schedule
        </Label>
        <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={addEntry}>
          <Plus className="h-3 w-3 mr-1" /> Add rate
        </Button>
      </div>

      {schedule.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No rate entries — add a rate to enable interest cost forecasting.</p>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border">
          {sorted.map(entry => (
            <div key={entry.id} className="flex items-center gap-2 px-3 py-2">
              <div className="flex-1 grid grid-cols-3 gap-2">
                <div className="space-y-0.5">
                  <Label className="text-[10px] text-muted-foreground">Effective from</Label>
                  <Input
                    className="h-7 text-xs"
                    type="date"
                    value={entry.effectiveFrom ? new Date(entry.effectiveFrom).toISOString().split('T')[0] : ''}
                    onChange={(e) => updateEntry(entry.id, { effectiveFrom: e.target.value ? new Date(e.target.value) : new Date() })}
                  />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[10px] text-muted-foreground">Rate (%)</Label>
                  <Input
                    className="h-7 text-xs"
                    type="number"
                    min={0}
                    step={0.01}
                    value={entry.rate || ''}
                    onChange={(e) => updateEntry(entry.id, { rate: Number.parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[10px] text-muted-foreground">Note (optional)</Label>
                  <Input
                    className="h-7 text-xs"
                    value={entry.note ?? ''}
                    onChange={(e) => updateEntry(entry.id, { note: e.target.value || undefined })}
                    placeholder="e.g. Base rate + 2%"
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0 mt-3"
                onClick={() => removeEntry(entry.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Drawdown Schedule sub-component ─────────────────────────────────────────

interface DrawdownScheduleProps {
  schedule: DrawdownEntry[];
  onChange: (updated: DrawdownEntry[]) => void;
}

function DrawdownSchedule({ schedule, onChange }: DrawdownScheduleProps) {
  function addEntry() {
    onChange([...schedule, blankDrawdown()]);
  }

  function updateEntry(id: string, partial: Partial<DrawdownEntry>) {
    onChange(schedule.map(e => e.id === id ? { ...e, ...partial } : e));
  }

  function removeEntry(id: string) {
    onChange(schedule.filter(e => e.id !== id));
  }

  const today = new Date();
  const sorted = [...schedule].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <Calendar className="h-3 w-3" />
          Drawdown schedule
          <span className="text-muted-foreground font-normal">(past & planned — no reconciliation required)</span>
        </Label>
        <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={addEntry}>
          <Plus className="h-3 w-3 mr-1" /> Add drawdown
        </Button>
      </div>

      {schedule.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No drawdowns recorded. Add past drawdowns for interest cost tracking, or planned future drawdowns to include them in the forecast.</p>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border">
          {sorted.map(entry => {
            const isFuture = new Date(entry.date) > today;
            return (
              <div key={entry.id} className="flex items-center gap-2 px-3 py-2">
                <div className="flex-1 grid grid-cols-3 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">
                      {isFuture ? '📅 Planned date' : 'Drawdown date'}
                    </Label>
                    <Input
                      className="h-7 text-xs"
                      type="date"
                      value={entry.date ? new Date(entry.date).toISOString().split('T')[0] : ''}
                      onChange={(e) => updateEntry(entry.id, { date: e.target.value ? new Date(e.target.value) : new Date() })}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Amount (£)</Label>
                    <Input
                      className="h-7 text-xs"
                      type="number"
                      min={0}
                      value={entry.amount || ''}
                      onChange={(e) => updateEntry(entry.id, { amount: Number.parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Description (optional)</Label>
                    <Input
                      className="h-7 text-xs"
                      value={entry.description ?? ''}
                      onChange={(e) => updateEntry(entry.id, { description: e.target.value || undefined })}
                      placeholder="e.g. Phase 2 project funding"
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0 mt-3"
                  onClick={() => removeEntry(entry.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Dynamic facility fields (per type) ──────────────────────────────────────

interface FacilityFieldsProps {
  facility: CreditFacility;
  onChange: (partial: Partial<CreditFacility>) => void;
  /** Zone 1 = connected account (drawn is read-only from feed). Zone 2 = manual (drawn is editable). */
  zone: 'zone1' | 'zone2';
  mode: ModeType;
}

function FacilityFields({ facility, onChange, zone, mode }: FacilityFieldsProps) {
  const { formatCurrency } = useFormatting();
  const drawn = facility.facilityDrawn ?? 0;
  const limit = facility.facilityLimit ?? 0;
  const computedHeadroom = limit - drawn;

  return (
    <div className="space-y-4">

      {/* ── Row 1: Type + Limit ── */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Facility type</Label>
          <Select
            value={facility.type}
            onValueChange={(v) => onChange({ type: v as CreditFacilityType })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overdraft">Overdraft</SelectItem>
              <SelectItem value="revolving_credit">Revolving Credit Facility</SelectItem>
              <SelectItem value="invoice_finance">Invoice Finance</SelectItem>
              <SelectItem value="asset_finance">Asset Finance</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">
            {facility.type === 'invoice_finance' ? 'Funding line ceiling' : 'Facility limit'}
          </Label>
          <Input
            className="h-8 text-xs"
            type="number"
            min={0}
            value={facility.facilityLimit || ''}
            onChange={(e) => onChange({ facilityLimit: Number.parseFloat(e.target.value) || 0 })}
            placeholder="0"
          />
        </div>
      </div>

      {/* ── Row 2: Drawn + Headroom ── */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1">
            Currently drawn
            {zone === 'zone1' && (
              <span className="text-[10px] text-muted-foreground">(from feed)</span>
            )}
          </Label>
          {zone === 'zone1' ? (
            <div className="h-8 flex items-center px-3 rounded-md border border-border bg-muted text-xs tabular-nums text-muted-foreground">
              {formatCurrency(drawn, 'full')}
            </div>
          ) : (
            <Input
              className="h-8 text-xs"
              type="number"
              min={0}
              value={facility.facilityDrawn || ''}
              onChange={(e) => onChange({ facilityDrawn: Number.parseFloat(e.target.value) || 0 })}
              placeholder="0"
            />
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Headroom (computed)</Label>
          <div className={`h-8 flex items-center px-3 rounded-md border border-border bg-muted text-xs font-medium tabular-nums ${computedHeadroom > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
            {formatCurrency(computedHeadroom, 'full')}
          </div>
        </div>
      </div>

      {/* ── Include in available cash toggle ── */}
      <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
        <input
          type="checkbox"
          checked={facility.includeInAvailableCash !== false}
          onChange={(e) => onChange({ includeInAvailableCash: e.target.checked })}
          className="rounded mt-0.5"
        />
        <div>
          <span className="text-xs font-medium">Include headroom in available cash</span>
          <p className="text-xs text-muted-foreground mt-0.5">
            {facility.includeInAvailableCash !== false
              ? 'Facility headroom is counted as available funds in the forecast.'
              : 'Facility is held as emergency reserve only — not counted as available funds.'}
          </p>
        </div>
      </label>

      {/* ── OVERDRAFT-specific fields ── */}
      {facility.type === 'overdraft' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Review / expiry date</Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={facility.facilityReviewDate ? new Date(facility.facilityReviewDate).toISOString().split('T')[0] : ''}
                onChange={(e) => onChange({ facilityReviewDate: e.target.value ? new Date(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Security note (optional)</Label>
              <Input
                className="h-8 text-xs"
                value={facility.securedOn ?? ''}
                onChange={(e) => onChange({ securedOn: e.target.value || undefined })}
                placeholder={mode === 'demo' ? 'e.g. Secured on commercial premises' : ''}
              />
            </div>
          </div>
          <InterestRateSchedule
            schedule={facility.interestRateSchedule ?? []}
            onChange={(s) => onChange({ interestRateSchedule: s })}
          />
        </div>
      )}

      {/* ── RCF-specific fields ── */}
      {facility.type === 'revolving_credit' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Availability period start</Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={facility.availabilityStart ? new Date(facility.availabilityStart).toISOString().split('T')[0] : ''}
                onChange={(e) => onChange({ availabilityStart: e.target.value ? new Date(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Availability period end</Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={facility.availabilityEnd ? new Date(facility.availabilityEnd).toISOString().split('T')[0] : ''}
                onChange={(e) => onChange({ availabilityEnd: e.target.value ? new Date(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Commitment fee % (on undrawn)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                step={0.01}
                value={facility.commitmentFeePercent ?? ''}
                onChange={(e) => onChange({ commitmentFeePercent: Number.parseFloat(e.target.value) || undefined })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Security note (optional)</Label>
              <Input
                className="h-8 text-xs"
                value={facility.securedOn ?? ''}
                onChange={(e) => onChange({ securedOn: e.target.value || undefined })}
                placeholder="e.g. Fixed and floating charge"
              />
            </div>
          </div>
          <InterestRateSchedule
            schedule={facility.interestRateSchedule ?? []}
            onChange={(s) => onChange({ interestRateSchedule: s })}
          />
          <DrawdownSchedule
            schedule={facility.drawdownSchedule ?? []}
            onChange={(s) => onChange({ drawdownSchedule: s })}
          />
        </div>
      )}

      {/* ── Invoice Finance-specific fields ── */}
      {facility.type === 'invoice_finance' && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Invoice finance type</Label>
            <Select
              value={facility.invoiceFinanceType ?? 'discounting'}
              onValueChange={(v) => onChange({ invoiceFinanceType: v as InvoiceFinanceType })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="discounting">Invoice Discounting (confidential — you collect)</SelectItem>
                <SelectItem value="factoring">Invoice Factoring (finance company collects)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              {facility.invoiceFinanceType === 'factoring'
                ? 'Factoring: the finance company collects from your customers. Customer payment behaviour does not apply — the factor\'s settlement schedule drives forecast timing.'
                : 'Discounting: you continue to collect from your customers. Customer payment behaviour applies normally in the forecast.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Advance rate (%)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={facility.advanceRatePercent ?? ''}
                onChange={(e) => onChange({ advanceRatePercent: Number.parseFloat(e.target.value) || undefined })}
                placeholder="e.g. 85"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Concentration limit (%)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={facility.concentrationLimitPercent ?? ''}
                onChange={(e) => onChange({ concentrationLimitPercent: Number.parseFloat(e.target.value) || undefined })}
                placeholder="e.g. 25"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Discount charge (%)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                step={0.01}
                value={facility.discountChargePercent ?? ''}
                onChange={(e) => onChange({ discountChargePercent: Number.parseFloat(e.target.value) || undefined })}
                placeholder="e.g. 2.8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Service fee (%)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                step={0.01}
                value={facility.serviceFeePercent ?? ''}
                onChange={(e) => onChange({ serviceFeePercent: Number.parseFloat(e.target.value) || undefined })}
                placeholder="e.g. 0.3"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Eligible debtor criteria (optional)</Label>
            <Input
              className="h-8 text-xs"
              value={facility.eligibleDebtorCriteria ?? ''}
              onChange={(e) => onChange({ eligibleDebtorCriteria: e.target.value || undefined })}
              placeholder="e.g. UK debtors only, max invoice age 90 days, no disputed invoices"
            />
          </div>

          {facility.invoiceFinanceType === 'factoring' && (
            <div className="space-y-1">
              <Label className="text-xs">Factor settlement schedule (optional)</Label>
              <Input
                className="h-8 text-xs"
                value={facility.factorSettlementSchedule ?? ''}
                onChange={(e) => onChange({ factorSettlementSchedule: e.target.value || undefined })}
                placeholder="e.g. Remits within 5 days of collection"
              />
            </div>
          )}

          <InterestRateSchedule
            schedule={facility.interestRateSchedule ?? []}
            onChange={(s) => onChange({ interestRateSchedule: s })}
          />
        </div>
      )}

      {/* ── Asset Finance-specific fields ── */}
      {facility.type === 'asset_finance' && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Asset finance type</Label>
            <Select
              value={facility.assetFinanceType ?? 'hire_purchase'}
              onValueChange={(v) => onChange({ assetFinanceType: v as AssetFinanceType })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hire_purchase">Hire Purchase (you own the asset at end of term)</SelectItem>
                <SelectItem value="finance_lease">Finance Lease (asset returned or refinanced at end)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Asset description</Label>
              <Input
                className="h-8 text-xs"
                value={facility.assetDescription ?? ''}
                onChange={(e) => onChange({ assetDescription: e.target.value || undefined })}
                placeholder={mode === 'demo' ? 'e.g. JCB 3CX Backhoe Loader (2024)' : 'Describe the asset being financed'}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Asset value (£)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                value={facility.assetValue ?? ''}
                onChange={(e) => onChange({ assetValue: Number.parseFloat(e.target.value) || undefined })}
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Finance start date</Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={facility.financeStartDate ? new Date(facility.financeStartDate).toISOString().split('T')[0] : ''}
                onChange={(e) => onChange({ financeStartDate: e.target.value ? new Date(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Term (months)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={1}
                value={facility.termMonths ?? ''}
                onChange={(e) => onChange({ termMonths: Number.parseInt(e.target.value) || undefined })}
                placeholder="e.g. 60"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End date (computed)</Label>
              <div className="h-8 flex items-center px-3 rounded-md border border-border bg-muted text-xs text-muted-foreground">
                {facility.financeStartDate && facility.termMonths
                  ? (() => {
                      const end = new Date(facility.financeStartDate);
                      end.setMonth(end.getMonth() + facility.termMonths);
                      return formatDateShort(end);
                    })()
                  : '—'}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Regular repayment (£)</Label>
              <Input
                className="h-8 text-xs"
                type="number"
                min={0}
                value={facility.repaymentAmount ?? ''}
                onChange={(e) => onChange({ repaymentAmount: Number.parseFloat(e.target.value) || undefined })}
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Repayment frequency</Label>
              <Select
                value={facility.repaymentFrequency ?? 'monthly'}
                onValueChange={(v) => onChange({ repaymentFrequency: v as 'monthly' | 'quarterly' | 'annual' })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {facility.assetFinanceType === 'hire_purchase' && (
              <div className="space-y-1">
                <Label className="text-xs">Balloon payment (£, optional)</Label>
                <Input
                  className="h-8 text-xs"
                  type="number"
                  min={0}
                  value={facility.balloonPayment ?? ''}
                  onChange={(e) => onChange({ balloonPayment: Number.parseFloat(e.target.value) || undefined })}
                  placeholder="0"
                />
              </div>
            )}
            {facility.assetFinanceType === 'finance_lease' && (
              <div className="space-y-1">
                <Label className="text-xs">Residual / handback value (£)</Label>
                <Input
                  className="h-8 text-xs"
                  type="number"
                  min={0}
                  value={facility.residualValue ?? ''}
                  onChange={(e) => onChange({ residualValue: Number.parseFloat(e.target.value) || undefined })}
                  placeholder="0"
                />
              </div>
            )}
          </div>

          <InterestRateSchedule
            schedule={facility.interestRateSchedule ?? []}
            onChange={(s) => onChange({ interestRateSchedule: s })}
          />
        </div>
      )}
    </div>
  );
}

// ─── Restriction row ──────────────────────────────────────────────────────────

interface RestrictionRowProps {
  restriction: RestrictedFund;
  onUpdate: (updated: RestrictedFund) => void;
  onRemove: () => void;
  isNew?: boolean;
  onEditComplete?: () => void;
  mode: ModeType;
}

function RestrictionRow({ restriction, onUpdate, onRemove, isNew, onEditComplete, mode }: RestrictionRowProps) {
  const { formatCurrency } = useFormatting();
  const [editing, setEditing] = useState(!!isNew);

  if (!editing) {
    return (
      <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${restrictionTypeBadgeColour(restriction.restrictionType)}`}>
              {restrictionTypeLabel(restriction.restrictionType)}
            </span>
            {restriction.isLegal && (
              <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                <AlertTriangle className="h-3 w-3" />
                Legally protected
              </span>
            )}
          </div>
          <p className="text-sm mt-0.5 text-foreground">{restriction.label}</p>
          <div className="flex items-center gap-4 mt-0.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{formatCurrency(restriction.restrictedAmount, 'full')}</span>
            {restriction.releaseDate && (
              <span>Releases {formatDateShort(restriction.releaseDate)}</span>
            )}
            {restriction.noticePeriodDays && (
              <span>{restriction.noticePeriodDays}-day notice period</span>
            )}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 px-2 text-xs">Edit</Button>
          <Button variant="ghost" size="sm" onClick={onRemove} className="h-7 px-2 text-xs text-destructive hover:text-destructive">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  const showReleaseDate = restriction.restrictionType === 'fixed_term' || restriction.restrictionType === 'escrow';
  const showNotice = restriction.restrictionType === 'notice_account';

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3 my-1">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Restriction type</Label>
          <Select
            value={restriction.restrictionType}
            onValueChange={(v) => onUpdate({ ...restriction, restrictionType: v as RestrictionType })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client_money">Client Money / Trust Fund</SelectItem>
              <SelectItem value="fixed_term">Fixed Term Deposit</SelectItem>
              <SelectItem value="notice_account">Notice Account</SelectItem>
              <SelectItem value="escrow">Escrow / Retention</SelectItem>
              <SelectItem value="minimum_balance">Minimum Balance Requirement</SelectItem>
              <SelectItem value="internal_reserve">Internal Reserve</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Restricted amount</Label>
          <Input
            className="h-8 text-xs"
            type="number"
            min={0}
            value={restriction.restrictedAmount || ''}
            onChange={(e) => onUpdate({ ...restriction, restrictedAmount: Number.parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Label / description</Label>
        <Input
          className="h-8 text-xs"
          value={restriction.label}
          onChange={(e) => onUpdate({ ...restriction, label: e.target.value })}
          placeholder={mode === 'demo' ? 'e.g. Thornton Developments client deposit' : ''}
        />
      </div>
      <div className="flex items-center gap-6 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={restriction.isLegal}
            onChange={(e) => onUpdate({ ...restriction, isLegal: e.target.checked })}
            className="rounded"
          />
          <span className="text-xs">Legally mandated restriction</span>
        </label>
        {showReleaseDate && (
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Release date</Label>
            <Input
              className="h-8 text-xs w-36"
              type="date"
              value={restriction.releaseDate ? new Date(restriction.releaseDate).toISOString().split('T')[0] : ''}
              onChange={(e) => onUpdate({ ...restriction, releaseDate: e.target.value ? new Date(e.target.value) : undefined })}
            />
          </div>
        )}
        {showNotice && (
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Notice period (days)</Label>
            <Input
              className="h-8 text-xs w-20"
              type="number"
              min={1}
              value={restriction.noticePeriodDays || ''}
              onChange={(e) => onUpdate({ ...restriction, noticePeriodDays: Number.parseInt(e.target.value) || undefined })}
              placeholder="90"
            />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => { if (isNew) onRemove(); else { setEditing(false); onEditComplete?.(); } }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => { setEditing(false); onEditComplete?.(); }}
          disabled={!restriction.label || restriction.restrictedAmount <= 0}
        >
          Save restriction
        </Button>
      </div>
    </div>
  );
}

// ─── Account card (Zone 1 — connected) ───────────────────────────────────────

interface AccountCardProps {
  account: BankAccount;
  onSave: (updated: BankAccount) => Promise<void>;
  mode: ModeType;
}

function AccountCard({ account, onSave, mode }: AccountCardProps) {
  const { formatCurrency } = useFormatting();
  const [local, setLocal] = useState<BankAccount>(account);
  const [facilityOpen, setFacilityOpen] = useState(!!account.creditFacility);
  const [restrictionsOpen, setRestrictionsOpen] = useState(!!(account.restrictedFunds?.length));
  const [removeFacilityDialog, setRemoveFacilityDialog] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newRestrictionIds, setNewRestrictionIds] = useState<Set<string>>(new Set());

  function updateFacility(partial: Partial<CreditFacility>) {
    setLocal(prev => ({
      ...prev,
      creditFacility: { ...(prev.creditFacility ?? blankFacility()), ...partial },
    }));
    setIsDirty(true);
  }

  function addFacility() {
    setLocal(prev => ({ ...prev, creditFacility: blankFacility() }));
    setFacilityOpen(true);
    setIsDirty(true);
  }

  function removeFacility() {
    setLocal(prev => { const n = { ...prev }; delete n.creditFacility; return n; });
    setFacilityOpen(false);
    setIsDirty(true);
    setRemoveFacilityDialog(false);
  }

  function addRestriction() {
    const newR: RestrictedFund = { ...blankRestriction(), id: `r-${Date.now()}` };
    setLocal(prev => ({ ...prev, restrictedFunds: [...(prev.restrictedFunds ?? []), newR] }));
    setNewRestrictionIds(prev => new Set(prev).add(newR.id));
    setRestrictionsOpen(true);
    setIsDirty(true);
  }

  function clearNewRestrictionId(id: string) {
    setNewRestrictionIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  }

  function updateRestriction(id: string, updated: RestrictedFund) {
    setLocal(prev => ({
      ...prev,
      restrictedFunds: (prev.restrictedFunds ?? []).map(r => r.id === id ? updated : r),
    }));
    setIsDirty(true);
  }

  function removeRestriction(id: string) {
    setLocal(prev => ({ ...prev, restrictedFunds: (prev.restrictedFunds ?? []).filter(r => r.id !== id) }));
    clearNewRestrictionId(id);
    setIsDirty(true);
  }

  const restricted = totalRestricted(local);
  const facilityHeadroom = headroom(local);
  const available = availableCash(local);

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{local.name}</CardTitle>
              <p className="text-sm text-muted-foreground">{local.bank} · {local.currency ?? 'GBP'}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-muted-foreground">Ledger balance</p>
            <p className="text-lg font-semibold tabular-nums">{formatCurrency(local.balance, 'full')}</p>
          </div>
        </div>

        {/* Available cash summary bar */}
        <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg border border-border bg-muted/40 p-3 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Restricted</p>
            <p className={`text-sm font-semibold tabular-nums ${restricted > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
              {restricted > 0 ? `−${formatCurrency(restricted, 'full')}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Facility headroom</p>
            <p className={`text-sm font-semibold tabular-nums ${facilityHeadroom > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {facilityHeadroom > 0 ? `+${formatCurrency(facilityHeadroom, 'full')}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium">Available cash</p>
            <p className="text-sm font-bold tabular-nums text-primary">{formatCurrency(available, 'full')}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">

        {/* ── Credit facility ── */}
        <Collapsible open={facilityOpen} onOpenChange={setFacilityOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
            >
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                Credit facility
                {local.creditFacility && (
                  <Badge variant="secondary" className="text-xs">
                    {facilityTypeLabel(local.creditFacility.type)} · {formatCurrency(local.creditFacility.facilityLimit, 'full')} limit
                  </Badge>
                )}
              </span>
              {facilityOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 space-y-3 px-1">
            {!local.creditFacility ? (
              <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                <span>No credit facility configured for this account</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addFacility}>
                  <Plus className="h-3 w-3 mr-1" /> Add facility
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <FacilityFields
                  facility={local.creditFacility}
                  onChange={updateFacility}
                  zone="zone1"
                  mode={mode}
                />
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    onClick={() => setRemoveFacilityDialog(true)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Remove facility
                  </Button>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* ── Restricted funds ── */}
        <Collapsible open={restrictionsOpen} onOpenChange={setRestrictionsOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
            >
              <span className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                Restricted funds
                {(local.restrictedFunds?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {local.restrictedFunds!.length} restriction{local.restrictedFunds!.length !== 1 ? 's' : ''} · {formatCurrency(restricted, 'full')} excluded
                  </Badge>
                )}
              </span>
              {restrictionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 px-1 space-y-1">
            {(local.restrictedFunds?.length ?? 0) === 0 ? (
              <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
                <span>No restricted funds on this account</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRestriction}>
                  <Plus className="h-3 w-3 mr-1" /> Add restriction
                </Button>
              </div>
            ) : (
              <>
                <div className="divide-y divide-border rounded-lg border border-border bg-background px-3">
                  {local.restrictedFunds!.map(r => (
                    <RestrictionRow
                      key={r.id}
                      restriction={r}
                      onUpdate={(updated) => updateRestriction(r.id, updated)}
                      onRemove={() => removeRestriction(r.id)}
                      isNew={newRestrictionIds.has(r.id)}
                      onEditComplete={() => clearNewRestrictionId(r.id)}
                      mode={mode}
                    />
                  ))}
                </div>
                <div className="flex justify-end pt-1">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addRestriction}>
                    <Plus className="h-3 w-3 mr-1" /> Add restriction
                  </Button>
                </div>
              </>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* ── Save ── */}
        {isDirty && (
          <div className="flex items-center justify-between pt-1 border-t border-border">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="h-3 w-3" />
              Unsaved changes on this account
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setLocal(account);
                  setIsDirty(false);
                  setFacilityOpen(!!account.creditFacility);
                  setRestrictionsOpen(!!(account.restrictedFunds?.length));
                }}
              >
                Discard
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={isSaving}
                onClick={() => {
                  setIsSaving(true);
                  onSave(local)
                    .then(() => { setIsDirty(false); })
                    .catch(() => { /* stay in edit mode */ })
                    .finally(() => { setIsSaving(false); });
                }}
              >
                <CheckCircle2 className="h-3 w-3 mr-1" /> {isSaving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={removeFacilityDialog} onOpenChange={setRemoveFacilityDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove credit facility?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {local.creditFacility ? facilityTypeLabel(local.creditFacility.type).toLowerCase() : 'facility'} configuration from <strong>{local.name}</strong>. The facility headroom will no longer be included in available cash calculations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeFacility} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove facility
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ─── Manual Facility Card (Zone 2) ────────────────────────────────────────────

interface ManualFacilityCardProps {
  facility: ManualFinancingFacility;
  onSave: (updated: ManualFinancingFacility) => void;
  onRemove: () => void;
  mode: ModeType;
}

function ManualFacilityCard({ facility, onSave, onRemove, mode }: ManualFacilityCardProps) {
  const { formatCurrency } = useFormatting();
  const [local, setLocal] = useState<ManualFinancingFacility>(facility);
  const [open, setOpen] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [removeDialog, setRemoveDialog] = useState(false);

  function updateFacility(partial: Partial<CreditFacility>) {
    setLocal(prev => ({ ...prev, facility: { ...prev.facility, ...partial } }));
    setIsDirty(true);
  }

  const drawn = local.facility.facilityDrawn ?? 0;
  const limit = local.facility.facilityLimit ?? 0;
  const facilityHeadroom = limit - drawn;

  return (
    <Card className="w-full border-dashed">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Landmark className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-base">{local.name}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {facilityTypeLabel(local.facility.type)} · Manually declared
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Limit</p>
              <p className="text-lg font-semibold tabular-nums">{formatCurrency(limit, 'full')}</p>
            </div>
          </div>
        </div>

        {/* Summary bar */}
        <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg border border-border bg-muted/40 p-3 text-center">
          <div>
            <p className="text-xs text-muted-foreground">Currently drawn</p>
            <p className="text-sm font-semibold tabular-nums text-amber-600">
              {drawn > 0 ? formatCurrency(drawn, 'full') : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Headroom</p>
            <p className={`text-sm font-semibold tabular-nums ${facilityHeadroom > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {facilityHeadroom > 0 ? `+${formatCurrency(facilityHeadroom, 'full')}` : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium">In available cash</p>
            <p className={`text-sm font-semibold ${local.facility.includeInAvailableCash !== false ? 'text-primary' : 'text-muted-foreground'}`}>
              {local.facility.includeInAvailableCash !== false ? 'Yes' : 'Emergency reserve'}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium bg-muted/50 hover:bg-muted transition-colors"
            >
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                Facility details
              </span>
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 space-y-3 px-1">
            {/* Name field */}
            <div className="space-y-1">
              <Label className="text-xs">Facility name</Label>
              <Input
                className="h-8 text-xs"
                value={local.name}
                onChange={(e) => { setLocal(prev => ({ ...prev, name: e.target.value })); setIsDirty(true); }}
                placeholder="e.g. HSBC Invoice Discounting Facility"
              />
            </div>

            <FacilityFields
              facility={local.facility}
              onChange={updateFacility}
              zone="zone2"
              mode={mode}
            />

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                className="h-8 text-xs"
                value={local.notes ?? ''}
                onChange={(e) => { setLocal(prev => ({ ...prev, notes: e.target.value || undefined })); setIsDirty(true); }}
                placeholder="Any additional context about this facility"
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {isDirty && (
          <div className="flex items-center justify-between pt-1 border-t border-border">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Info className="h-3 w-3" />
              Unsaved changes
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setLocal(facility); setIsDirty(false); }}
              >
                Discard
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={!local.name}
                onClick={() => { onSave(local); setIsDirty(false); }}
              >
                <CheckCircle2 className="h-3 w-3 mr-1" /> Save changes
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-destructive hover:text-destructive"
            onClick={() => setRemoveDialog(true)}
          >
            <Trash2 className="h-3 w-3 mr-1" /> Remove facility
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={removeDialog} onOpenChange={setRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove financing facility?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{local.name}</strong> from the forecast. Any future cash events associated with this facility will no longer be projected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove facility
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function BankAccountsPage() {
  const { mode } = useApp();
  const { toast } = useToast();
  const { formatCurrency } = useFormatting();

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [manualFacilities, setManualFacilities] = useState<ManualFinancingFacility[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'live') {
      setIsLoading(true);
      setError(null);
      getAllBankAccounts()
        .then((response) => {
          const mapped = response.bankAccounts.map(mapBankAccountFromApi);
          setAccounts(mapped);
          setManualFacilities([]);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to fetch bank accounts');
          setAccounts([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setAccounts(BANK_ACCOUNTS.map(a => ({ ...a })));
      setManualFacilities(MANUAL_FINANCING_FACILITIES.map(f => ({ ...f })));
      setError(null);
    }
  }, [mode]);

  async function handleSave(updated: BankAccount): Promise<void> {
    if (mode === 'live') {
      const numericId = Number(updated.id);
      if (Number.isNaN(numericId)) return;
      const request = mapBankAccountToUpdateRequest(updated);
      const response = await updateBankAccount(numericId, request)
        .catch((err) => {
          console.error('Failed to update bank account:', err);
          const description = err instanceof ApiError
            ? err.message
            : 'Failed to save bank account changes. Please try again.';
          toast({ title: 'Error', description, variant: 'destructive' });
          throw err;
        });
      const mapped = mapBankAccountFromApi(response);
      setAccounts(prev => prev.map(a => a.id === mapped.id ? mapped : a));
      toast({ title: 'Saved', description: `Bank account "${mapped.name}" updated successfully.` });
    } else {
      setAccounts(prev => prev.map(a => a.id === updated.id ? updated : a));
    }
  }

  function handleSaveManualFacility(updated: ManualFinancingFacility) {
    setManualFacilities(prev => prev.map(f => f.id === updated.id ? updated : f));
  }

  function handleRemoveManualFacility(id: string) {
    setManualFacilities(prev => prev.filter(f => f.id !== id));
  }

  function handleAddManualFacility() {
    const newFacility: ManualFinancingFacility = {
      id: `manual-${Date.now()}`,
      name: 'New Financing Facility',
      facility: blankFacility(),
    };
    setManualFacilities(prev => [...prev, newFacility]);
  }

  const totalBalance = accounts.reduce((s, a) => s + a.balance, 0);
  const totalRestrictedAll = accounts.reduce((s, a) => s + totalRestricted(a), 0);
  const totalHeadroomAll = accounts.reduce((s, a) => s + headroom(a), 0);
  const manualHeadroom = manualFacilities
    .filter(f => f.facility.includeInAvailableCash !== false)
    .reduce((s, f) => s + Math.max(0, (f.facility.facilityLimit ?? 0) - (f.facility.facilityDrawn ?? 0)), 0);
  const totalAvailable = totalBalance - totalRestrictedAll + totalHeadroomAll + manualHeadroom;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bank Accounts</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage connected bank accounts, credit facilities, account restrictions, and manually declared financing arrangements. These settings affect how available cash is calculated across forecasts and dashboards.
        </p>
      </div>

      {/* Organisation-level summary */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Accounts</p>
              <p className="text-xl font-bold">{accounts.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total ledger balance</p>
              <p className="text-xl font-bold tabular-nums">{formatCurrency(totalBalance, 'full')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total restricted</p>
              <p className="text-xl font-bold tabular-nums text-red-600">
                {totalRestrictedAll > 0 ? `−${formatCurrency(totalRestrictedAll, 'full')}` : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Total available cash</p>
              <p className="text-xl font-bold tabular-nums text-primary">{formatCurrency(totalAvailable, 'full')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Bank accounts are connected via your configured data sources</p>
          <p className="text-xs mt-0.5 text-amber-700">
            To add or remove accounts, update your data source connections in Admin → Data Sources. Facility and restriction details configured here are supplementary — they do not affect the underlying connection.
          </p>
        </div>
      </div>

      {/* ── ZONE 1: Connected accounts ── */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Connected Accounts</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Accounts connected via your data sources. Balances and drawn amounts are sourced automatically. Configure credit facilities, interest rate schedules, and fund restrictions below.
          </p>
        </div>

        {isLoading && (
          <Card className="bg-muted/30">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Loading bank accounts…
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-destructive bg-destructive/5">
            <CardContent className="py-4 text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </CardContent>
          </Card>
        )}

        {!isLoading && !error && accounts.map(account => (
          <AccountCard
            key={account.id}
            account={account}
            onSave={handleSave}
            mode={mode}
          />
        ))}
      </div>

      {/* ── ZONE 2: Manually declared financing facilities ── */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Manually Declared Financing Facilities</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Declare financing arrangements that are not already represented in your connected data sources.
          </p>
        </div>

        {/* Zone 2 disclaimer banner */}
        <Collapsible defaultOpen>
          <div className="rounded-lg border border-blue-200 bg-blue-50">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-start justify-between p-3 text-left"
              >
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-sm font-medium text-blue-800">Only add facilities here if they are not already in your connected data sources.</p>
                </div>
                <ChevronDown className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="text-xs text-blue-700 px-3 pb-3 leading-relaxed">
                Financing arrangements already configured in your connected data sources — such as overdrafts, revolving credit facilities, or asset finance agreements — are brought across automatically and do not need to be entered here.
                Use this section for financing arrangements that exist outside your connected systems: invoice finance facilities, standalone loans, equity investments, or any arrangement your connected sources cannot represent. Duplicating a connected facility here will distort your available cash calculation.
              </p>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {manualFacilities.length === 0 ? (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
            <span>No manually declared financing facilities</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleAddManualFacility}>
              <Plus className="h-3 w-3 mr-1" /> Add facility
            </Button>
          </div>
        ) : (
          <>
            {manualFacilities.map(f => (
              <ManualFacilityCard
                key={f.id}
                facility={f}
                onSave={handleSaveManualFacility}
                onRemove={() => handleRemoveManualFacility(f.id)}
                mode={mode}
              />
            ))}
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleAddManualFacility}>
                <Plus className="h-3 w-3 mr-1" /> Add facility
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
